import {
  ActionIcon,
  Box,
  Button,
  Divider,
  FileButton,
  Grid,
  Group,
  Image,
  Select,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import {
  IconCheck,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useEffect, useState, useMemo, memo, useCallback } from "react";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import QuillEditor from "../../../common/QuillEditor";
import ButtonClose from "../../../common/ButtonClose/ButtonClose";
import { getDataProvice } from "../../../api/ApiAddress";
import { TblWarranteeBrandCategory } from "../../../model/TblWarranteeBrandCategory";
import { convertImageDomain } from "../../../const/converhandle";
import Repository from "../../../_base/helper/HttpHelper";
import { API_ROUTE } from "../../../const/apiRoute";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  getDetailBrandCategory,
  modifyBrandCategory,
} from "../../../api/ApiBrandCategory";
import { uploadFile } from "../../../api/ApiProduct";
import { apiPOS } from "../../../library/axios";
import BrandSelectModal from "./BrandSelectModal";

const normalizeProvinceName = (name: string | null | undefined): string => {
  if (!name) return "";
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^(thanh pho|tinh|tp\.?)\s+/i, "")
    .replace(/\s+/g, "")
    .trim();
};

const replaceProvinceInAddress = (
  address: string,
  oldProv: string,
  newProv: string
): string => {
  if (!address) return newProv;
  if (!oldProv) {
    return address.endsWith(newProv) ? address : `${address}, ${newProv}`;
  }

  if (address.includes(oldProv)) {
    return address.replace(oldProv, newProv);
  }

  const normOld = normalizeProvinceName(oldProv);
  if (!normOld) return address;

  const variations = [
    oldProv,
    `Thành phố ${oldProv}`,
    `Tỉnh ${oldProv}`,
    `Tp. ${oldProv}`,
    `Tp ${oldProv}`,
    oldProv.replace(/^(Thành phố|Tỉnh|Tp\.?)\s+/i, "").trim()
  ].filter(Boolean);

  for (const variant of variations) {
    if (address.toLowerCase().includes(variant.toLowerCase())) {
      const regex = new RegExp(variant, "ig");
      return address.replace(regex, newProv);
    }
  }

  const normAddr = normalizeProvinceName(address);
  if (normAddr === normOld) {
    return newProv;
  }

  return address;
};

const detectCommuneAndStreet = (
  address: string,
  communesList: { value: string; label: string }[],
  provinceLabel: string
) => {
  if (!address) return { tempCommuneId: "", tempStreet: "" };
  const addrLower = address.toLowerCase();
  
  // Sort communes by label length descending to match the most specific name first (e.g. "Phường 10" before "Phường 1")
  const sortedCommunes = [...communesList].sort((a, b) => b.label.length - a.label.length);
  const matchedCommune = sortedCommunes.find((c) => {
    const commLabelLower = c.label.toLowerCase();
    return addrLower.includes(commLabelLower);
  });

  if (matchedCommune) {
    const commLabelLower = matchedCommune.label.toLowerCase();
    const commIndex = addrLower.indexOf(commLabelLower);
    let streetPart = "";
    if (commIndex > 0) {
      streetPart = address.substring(0, commIndex).trim().replace(/,\s*$/, "");
    } else {
      streetPart = address.replace(new RegExp(matchedCommune.label, "i"), "").trim();
    }
    
    if (provinceLabel) {
      streetPart = streetPart.replace(new RegExp(provinceLabel, "i"), "").trim().replace(/,\s*$/, "");
    }
    
    return {
      tempCommuneId: matchedCommune.value,
      tempStreet: streetPart
    };
  }

  let streetPart = address;
  if (provinceLabel) {
    const provIndex = addrLower.indexOf(provinceLabel.toLowerCase());
    if (provIndex > 0) {
      streetPart = address.substring(0, provIndex).trim().replace(/,\s*$/, "");
    }
  }
  return {
    tempCommuneId: "",
    tempStreet: streetPart
  };
};

const WarrantyCenterRow = memo(
  ({
    index,
    center,
    provinceOptions,
    provinces,
    onChange,
    onRemove,
  }: {
    index: number;
    center: any;
    provinceOptions: any[];
    provinces: any[];
    onChange: (index: number, field: string, value: any) => void;
    onRemove: () => void;
  }) => {
    const [localAddress, setLocalAddress] = useState(center.address || "");
    const [localProductType, setLocalProductType] = useState(
      center.productType || "",
    );
    const [localPhoneNumber, setLocalPhoneNumber] = useState(
      center.phoneNumber || "",
    );
    const [communes, setCommunes] = useState<
      { value: string; label: string }[]
    >([]);
    const [loadingCommunes, setLoadingCommunes] = useState(false);

    useEffect(() => {
      setLocalAddress(center.address || "");
    }, [center.address]);

    useEffect(() => {
      setLocalProductType(center.productType || "");
    }, [center.productType]);

    useEffect(() => {
      setLocalPhoneNumber(center.phoneNumber || "");
    }, [center.phoneNumber]);

    useEffect(() => {
      const fetchCommunes = async () => {
        if (!center.province) {
          setCommunes([]);
          return;
        }
        const provinceObj = provinces.find((p) => p.label === center.province);
        const provinceId = provinceObj ? provinceObj.value : "";
        if (!provinceId) {
          setCommunes([]);
          return;
        }
        setLoadingCommunes(true);
        try {
          const response = await apiPOS.get(
            `/api-mdm/api/v1/TblDmCommuneMoi/get-select?ProvinceIds=${provinceId}`,
          );
          if (response?.data?.success && response.data.data) {
            const fetchedCommunes = response.data.data.map((item: any) => ({
              value: item.value,
              label: item.text,
            }));
            setCommunes(fetchedCommunes);

            // Auto-detect commune and street if they are not defined but address exists
            if (center.address && !center.tempCommuneId && !center.tempStreet) {
              const provLabel = center.province || "";
              const detection = detectCommuneAndStreet(center.address, fetchedCommunes, provLabel);
              if (detection.tempCommuneId) {
                onChange(index, "tempCommuneId", detection.tempCommuneId);
              }
              if (detection.tempStreet) {
                onChange(index, "tempStreet", detection.tempStreet);
              }
            }
          } else {
            setCommunes([]);
          }
        } catch (error) {
          console.error("Error fetching communes:", error);
          setCommunes([]);
        } finally {
          setLoadingCommunes(false);
        }
      };
      fetchCommunes();
    }, [center.province, provinces]);

    return (
      <Table.Tr style={{ "&:hover": { background: "#fcfcfc" } }}>
        <Table.Td
          align="center"
          style={{ fontWeight: 600, color: "#495057", padding: "12px 8px" }}
        >
          {index + 1}
        </Table.Td>
        <Table.Td style={{ padding: "12px 8px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div>
              <Text
                size="10px"
                fw={700}
                c="dimmed"
                mb={3}
                style={{ letterSpacing: "0.3px" }}
              >
                1. TỈNH/THÀNH LỌC WEB:
              </Text>
              <Select
                size="xs"
                placeholder="Chọn tỉnh/thành để khách hàng lọc..."
                data={provinceOptions}
                searchable
                clearable
                value={center.province || ""}
                onChange={(val) => {
                  const oldProvince = center.province || "";
                  onChange(index, "province", val || null);
                  onChange(index, "tempCommuneId", "");
                  onChange(index, "tempStreet", "");
                  if (val) {
                    const newAddress = replaceProvinceInAddress(center.address || "", oldProvince, val);
                    onChange(index, "address", newAddress);
                  }
                }}
                styles={{
                  input: {
                    fontSize: "13px",
                    fontWeight: 500,
                    borderRadius: "6px",
                  },
                }}
              />
            </div>
            <div>
              <Text
                size="10px"
                fw={700}
                c="dimmed"
                mb={3}
                style={{ letterSpacing: "0.3px" }}
              >
                2. ĐỊA CHỈ HIỂN THỊ CHI TIẾT:
              </Text>
              <TextInput
                size="xs"
                placeholder="Nhập đầy đủ số nhà, tên đường, quận, huyện..."
                value={localAddress}
                onChange={(e) => setLocalAddress(e.currentTarget.value)}
                onBlur={() => {
                  onChange(index, "address", localAddress);
                  const provLabel = center.province || "";
                  const detection = detectCommuneAndStreet(localAddress, communes, provLabel);
                  onChange(index, "tempCommuneId", detection.tempCommuneId);
                  onChange(index, "tempStreet", detection.tempStreet);
                }}
                styles={{
                  input: { fontSize: "13px", borderRadius: "6px" },
                }}
              />
            </div>
          </div>
        </Table.Td>
        <Table.Td style={{ verticalAlign: "top", padding: "12px 8px" }}>
          <div
            style={{
              minHeight: "84px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <TextInput
              size="xs"
              placeholder="Ví dụ: Laptop, PC, Màn hình..."
              value={localProductType}
              onChange={(e) => setLocalProductType(e.currentTarget.value)}
              onBlur={() => {
                onChange(index, "productType", localProductType);
              }}
              styles={{
                input: { fontSize: "13px", borderRadius: "6px" },
              }}
            />
          </div>
        </Table.Td>
        <Table.Td style={{ verticalAlign: "top", padding: "12px 8px" }}>
          <div
            style={{
              minHeight: "84px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <TextInput
              size="xs"
              placeholder="Ví dụ: 1900 1900"
              value={localPhoneNumber}
              onChange={(e) => setLocalPhoneNumber(e.currentTarget.value)}
              onBlur={() => {
                onChange(index, "phoneNumber", localPhoneNumber);
              }}
              styles={{
                input: { fontSize: "13px", borderRadius: "6px" },
              }}
            />
          </div>
        </Table.Td>
        <Table.Td
          style={{
            textAlign: "center",
            verticalAlign: "middle",
            padding: "12px 8px",
          }}
        >
          <ActionIcon
            color="red"
            variant="light"
            onClick={onRemove}
            style={{ borderRadius: "6px" }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Table.Td>
      </Table.Tr>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.index === nextProps.index &&
      prevProps.center.province === nextProps.center.province &&
      prevProps.center.address === nextProps.center.address &&
      prevProps.center.productType === nextProps.center.productType &&
      prevProps.center.phoneNumber === nextProps.center.phoneNumber &&
      prevProps.center.tempCommuneId === nextProps.center.tempCommuneId &&
      prevProps.center.tempStreet === nextProps.center.tempStreet &&
      prevProps.provinceOptions === nextProps.provinceOptions &&
      prevProps.provinces === nextProps.provinces
    );
  },
);

const EditView = ({ onSearch, id }: any) => {
  const [valuePolicy, setValuePolicy] = useState("");

  const form = useForm<any>({
    initialValues: {
      id: 0,
      name: "",
      logoImg: "",
      status: "A", // "A" for Active, "I" for Inactive
      orderNumber: 0,
      content: "",
      tblWarrantyCenterCommands: [],
      metaTitle: "",
      metaKeyword: "",
      metaDescription: "",
      url: "",
      thumbnail: "",
    },
    validate: {
      name: (value) => (!value ? "Tên hãng không được để trống" : null),
      logoImg: (value) => (!value ? "Link logo không được để trống" : null),
    },
  });

  const [provinces, setProvinces] = useState<
    { value: number; label: string }[]
  >([]);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);

  const provinceOptions = useMemo(() => {
    return provinces.map((p) => ({
      value: p.label,
      label: p.label,
    }));
  }, [provinces]);

  const memoizedQuillEditor = useMemo(() => {
    return (
      <QuillEditor
        toolbarId="warranty-policy-edit"
        setValue={setValuePolicy}
        description={valuePolicy}
      />
    );
  }, [valuePolicy]);

  // Fetch record detail on load
  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;

      let loadedProvinces = provinces;
      // Load provinces first so matching works
      if (provinces.length === 0) {
        const provinceResponse = await apiPOS.get(
          "/api-mdm/api/v1/TblDmProvinceMoi/get-list?Skip=0&Take=250",
        );
        if (provinceResponse?.data?.success && provinceResponse.data.data) {
          loadedProvinces = provinceResponse.data.data.map((p: any) => ({
            value: p.id,
            label: p.name,
          }));
          setProvinces(loadedProvinces);
        }
      }

      const res = await getDetailBrandCategory(id);
      const record =
        res && res.success && res.data
          ? res.data
          : res && res.data
            ? res.data
            : res;
      if (record) {
        const rawCenters =
          record.tblWarrantyCenterModels ||
          record.tblWarrantyCenterCommands ||
          [];
        const centers = rawCenters.map((center: any) => {
          if (center.province) {
            let matchedLabel = center.province;
            if (!isNaN(Number(center.province))) {
              const matchedProv = loadedProvinces.find(
                (p) => p.value === Number(center.province),
              );
              if (matchedProv) {
                matchedLabel = matchedProv.label;
              }
            } else {
              const targetNormalized = normalizeProvinceName(center.province);
              const matchedProv = loadedProvinces.find(
                (p) => normalizeProvinceName(p.label) === targetNormalized,
              );
              if (matchedProv) {
                matchedLabel = matchedProv.label;
              }
            }
            return {
              ...center,
              province: matchedLabel,
            };
          }
          return center;
        });
        form.setValues({
          ...record,
          tblWarrantyCenterCommands: centers,
        });
        setValuePolicy(record.content || "");
      }
    };
    fetchDetail();
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
    };
    fetchData();
  }, []);

  const handleBrandChange = (brand: { value: string; label: string; logo: string }) => {
    form.setFieldValue("logoImg", brand.logo);
    form.setFieldValue("name", brand.label);
    form.setFieldValue("url", convertToSlug(brand.label));
  };

  const handleEdit = async (values: TblWarranteeBrandCategory) => {
    values.content = valuePolicy;
    const sanitizedCenters = (values.tblWarrantyCenterCommands || []).map(
      (center: any) => {
        const { tempCommuneId, tempStreet, ...rest } = center;
        return rest;
      },
    );
    const payload = {
      ...values,
      tblWarrantyCenterCommands: sanitizedCenters,
    };
    console.log("Saving Edited Brand Warranty Category:", payload);
    const res = await modifyBrandCategory(payload);
    if (res && res.success) {
      onSearch();
      modals.closeAll();
    }
  };

  const handleRowChange = useCallback(
    (index: number, field: string, value: any) => {
      form.setFieldValue(`tblWarrantyCenterCommands.${index}.${field}`, value);
    },
    [form],
  );

  const addRow = () => {
    form.insertListItem("tblWarrantyCenterCommands", {
      id: 0,
      name: "",
      province: null,
      address: "",
      productType: "",
      phoneNumber: "",
      email: "",
      tempCommuneId: "",
      tempStreet: "",
    });
  };

  return (
    <Box
      component="form"
      miw={1000}
      maw={1200}
      mx="auto"
      onSubmit={form.onSubmit(handleEdit)}
    >
      <Box
        style={{
          background: "#f8f9fa",
          padding: "12px 18px",
          borderRadius: "8px",
          borderLeft: "4px solid #495057",
          marginBottom: "16px",
          marginTop: "8px",
        }}
      >
        <Box>
          <Text
            size="sm"
            fw={700}
            c="gray.8"
            style={{ letterSpacing: "0.5px" }}
          >
            1. THÔNG TIN HÃNG BẢO HÀNH
          </Text>
          <Text size="xs" c="dimmed">
            Chỉnh sửa thông tin thương hiệu, trạng thái hoạt động, hình đại diện
            và logo hãng: {form.values.name}
          </Text>
        </Box>
      </Box>
      <Grid>
        <Grid.Col span={12}>
          <TextInput
            label="Chọn thương hiệu (Tự động điền)"
            placeholder="Nhấp để chọn thương hiệu..."
            value={form.values.name ? form.values.name : ""}
            readOnly
            onClick={() => setIsBrandModalOpen(true)}
            rightSection={
              <IconSearch
                size={16}
                style={{ cursor: "pointer" }}
                onClick={() => setIsBrandModalOpen(true)}
              />
            }
            styles={{ input: { cursor: "pointer" } }}
          />
          <BrandSelectModal
            opened={isBrandModalOpen}
            onClose={() => setIsBrandModalOpen(false)}
            onSelect={handleBrandChange}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Tên hãng"
            placeholder="Nhập tên hãng"
            withAsterisk
            value={form.values.name || ""}
            onChange={(e) => {
              const val = e.currentTarget.value;
              form.setFieldValue("name", val);
              form.setFieldValue("url", convertToSlug(val));
            }}
            error={form.errors.name}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Select
            label="Trạng thái"
            data={[
              { value: "A", label: "Hoạt động" },
              { value: "I", label: "Không hoạt động" },
            ]}
            {...form.getInputProps("status")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Link Logo"
            placeholder="Đường dẫn logo hãng"
            withAsterisk
            {...form.getInputProps("logoImg")}
            rightSection={
              <FileButton
                onChange={async (file) => {
                  if (file) {
                    const result = await uploadFile([file], "warrantee");
                    if (result && result.success && result.data?.[0]?.url) {
                      form.setFieldValue("logoImg", result.data[0].url);
                    }
                  }
                }}
                accept="image/*"
              >
                {(props) => (
                  <Button {...props} size="xs" variant="subtle" px={5}>
                    Tải lên
                  </Button>
                )}
              </FileButton>
            }
            rightSectionWidth={70}
          />
          {form.values.logoImg && (
            <Box
              mt="xs"
              style={{
                position: "relative",
                width: 100,
                height: 100,
                border: "1px solid #e9ecef",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <Image
                src={form.values.logoImg}
                alt="Logo Preview"
                width={100}
                height={100}
                fit="contain"
                fallbackSrc="https://via.placeholder.com/100?text=Logo"
              />
              <ActionIcon
                size="xs"
                color="red"
                variant="filled"
                style={{ position: "absolute", top: 2, right: 2, zIndex: 1 }}
                onClick={() => form.setFieldValue("logoImg", "")}
              >
                <IconTrash size={12} />
              </ActionIcon>
            </Box>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Hình đại diện (Thumb)"
            placeholder="Dán link ảnh đại diện"
            {...form.getInputProps("thumbnail")}
            rightSection={
              <FileButton
                onChange={async (file) => {
                  if (file) {
                    const result = await uploadFile([file], "warrantee");
                    if (result && result.success && result.data?.[0]?.url) {
                      form.setFieldValue("thumbnail", result.data[0].url);
                    }
                  }
                }}
                accept="image/*"
              >
                {(props) => (
                  <Button {...props} size="xs" variant="subtle" px={5}>
                    Tải lên
                  </Button>
                )}
              </FileButton>
            }
            rightSectionWidth={70}
          />
          {form.values.thumbnail && (
            <Box
              mt="xs"
              style={{
                position: "relative",
                width: 120,
                height: 80,
                border: "1px solid #e9ecef",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <Image
                src={form.values.thumbnail}
                alt="Thumbnail Preview"
                width={120}
                height={80}
                fit="contain"
                fallbackSrc="https://via.placeholder.com/120x80?text=Thumb"
              />
              <ActionIcon
                size="xs"
                color="red"
                variant="filled"
                style={{ position: "absolute", top: 2, right: 2, zIndex: 1 }}
                onClick={() => form.setFieldValue("thumbnail", "")}
              >
                <IconTrash size={12} />
              </ActionIcon>
            </Box>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Thứ tự hiển thị"
            type="number"
            {...form.getInputProps("orderNumber")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Đường dẫn yêu cầu"
            placeholder="vd: /dell-warranty-info"
            {...form.getInputProps("url")}
          />
        </Grid.Col>
      </Grid>

      <Box
        style={{
          background: "#f8f9fa",
          padding: "12px 18px",
          borderRadius: "8px",
          borderLeft: "4px solid #fcc419",
          marginBottom: "16px",
          marginTop: "24px",
        }}
      >
        <Box>
          <Text
            size="sm"
            fw={700}
            c="yellow.9"
            style={{ letterSpacing: "0.5px" }}
          >
            2. CHÍNH SÁCH BẢO HÀNH
          </Text>
          <Text size="xs" c="dimmed">
            Soạn thảo văn bản chi tiết về chính sách, điều khoản và quy định bảo
            hành của hãng
          </Text>
        </Box>
      </Box>
      {memoizedQuillEditor}

      <Box
        style={{
          background: "#f8f9fa",
          padding: "12px 18px",
          borderRadius: "8px",
          borderLeft: "4px solid #1c7ed6",
          marginBottom: "16px",
          marginTop: "24px",
        }}
      >
        <Group justify="space-between">
          <Box>
            <Text
              size="sm"
              fw={700}
              c="blue.7"
              style={{ letterSpacing: "0.5px" }}
            >
              3. DANH SÁCH TRUNG TÂM BẢO HÀNH
            </Text>
            <Text size="xs" c="dimmed">
              Quản lý danh sách các điểm tiếp nhận bảo hành của hãng
            </Text>
          </Box>
          <Group gap="xs">
            <Button
              variant="filled"
              color="blue"
              leftSection={<IconPlus size={14} />}
              onClick={addRow}
              size="xs"
              style={{ borderRadius: "6px" }}
            >
              Thêm dòng
            </Button>
            <Button
              variant="light"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() =>
                form.setFieldValue("tblWarrantyCenterCommands", [])
              }
              size="xs"
              disabled={
                !form.values.tblWarrantyCenterCommands ||
                form.values.tblWarrantyCenterCommands.length === 0
              }
              style={{ borderRadius: "6px" }}
            >
              Xóa tất cả dòng
            </Button>
          </Group>
        </Group>
      </Box>

      <Box
        style={{
          overflowX: "auto",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          overflow: "hidden",
          marginBottom: "24px",
        }}
      >
        <Table
          withColumnBorders
          withRowBorders
          style={{ minWidth: "100%", borderCollapse: "collapse" }}
        >
          <Table.Thead style={{ background: "#f8f9fa" }}>
            <Table.Tr>
              <Table.Th
                w={60}
                style={{
                  textAlign: "center",
                  fontSize: "11px",
                  textTransform: "uppercase",
                  color: "#495057",
                  fontWeight: 700,
                  padding: "12px 8px",
                }}
              >
                Số
              </Table.Th>
              <Table.Th
                style={{
                  minWidth: 420,
                  fontSize: "11px",
                  textTransform: "uppercase",
                  color: "#495057",
                  fontWeight: 700,
                  padding: "12px 8px",
                }}
              >
                Địa chỉ & Tỉnh/Thành
              </Table.Th>
              <Table.Th
                style={{
                  minWidth: 220,
                  fontSize: "11px",
                  textTransform: "uppercase",
                  color: "#495057",
                  fontWeight: 700,
                  padding: "12px 8px",
                }}
              >
                Sản phẩm nhận bảo hành
              </Table.Th>
              <Table.Th
                style={{
                  minWidth: 160,
                  fontSize: "11px",
                  textTransform: "uppercase",
                  color: "#495057",
                  fontWeight: 700,
                  padding: "12px 8px",
                }}
              >
                Điện thoại / Fax
              </Table.Th>
              <Table.Th w={50}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {form.values.tblWarrantyCenterCommands?.map(
              (center: any, index: number) => (
                <WarrantyCenterRow
                  key={index}
                  index={index}
                  center={center}
                  provinceOptions={provinceOptions}
                  provinces={provinces}
                  onChange={handleRowChange}
                  onRemove={() =>
                    form.removeListItem("tblWarrantyCenterCommands", index)
                  }
                />
              ),
            )}
            {(!form.values.tblWarrantyCenterCommands ||
              form.values.tblWarrantyCenterCommands.length === 0) && (
              <Table.Tr>
                <Table.Td
                  colSpan={5}
                  align="center"
                  style={{ padding: "24px" }}
                >
                  <Text size="sm" c="dimmed" fw={500}>
                    Chưa có trung tâm bảo hành nào. Nhấn "Thêm dòng" để cấu hình
                    địa chỉ.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Box>

      <Box
        style={{
          background: "#f8f9fa",
          padding: "12px 18px",
          borderRadius: "8px",
          borderLeft: "4px solid #40c057",
          marginBottom: "16px",
          marginTop: "24px",
        }}
      >
        <Box>
          <Text
            size="sm"
            fw={700}
            c="green.8"
            style={{ letterSpacing: "0.5px" }}
          >
            4. CẤU HÌNH SEO
          </Text>
          <Text size="xs" c="dimmed">
            Tối ưu hóa các thẻ Meta hỗ trợ công cụ tìm kiếm Google hiển thị tốt
            nhất
          </Text>
        </Box>
      </Box>

      <Grid style={{ marginBottom: "24px" }}>
        <Grid.Col span={6}>
          <TextInput
            label="Tiêu đề Meta"
            placeholder="Nhập tiều đề SEO..."
            {...form.getInputProps("metaTitle")}
            styles={{
              label: { fontWeight: 600, fontSize: "13px" },
              input: { borderRadius: "6px" },
            }}
          />
          <Group justify="space-between" mt={4} px={2}>
            <Text size="11px" c="dimmed">
              Tiêu đề trang khi hiển thị trên Google
            </Text>
            <Group gap={4}>
              <Text
                size="10px"
                fw={600}
                style={{
                  padding: "1px 6px",
                  background: "#e9ecef",
                  borderRadius: "4px",
                  color: "#495057",
                }}
              >
                {getWordCount(form.values.metaTitle)} từ
              </Text>
              <Text
                size="10px"
                fw={600}
                style={{
                  padding: "1px 6px",
                  background: "#e9ecef",
                  borderRadius: "4px",
                  color: "#495057",
                }}
              >
                {getCharCount(form.values.metaTitle)} ký tự
              </Text>
            </Group>
          </Group>
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Từ khóa Meta"
            placeholder="Từ khóa 1, từ khóa 2..."
            {...form.getInputProps("metaKeyword")}
            styles={{
              label: { fontWeight: 600, fontSize: "13px" },
              input: { borderRadius: "6px" },
            }}
          />
          <Group justify="space-between" mt={4} px={2}>
            <Text size="11px" c="dimmed">
              Các từ khóa tìm kiếm liên quan
            </Text>
            <Group gap={4}>
              <Text
                size="10px"
                fw={600}
                style={{
                  padding: "1px 6px",
                  background: "#e9ecef",
                  borderRadius: "4px",
                  color: "#495057",
                }}
              >
                {getWordCount(form.values.metaKeyword)} từ
              </Text>
              <Text
                size="10px"
                fw={600}
                style={{
                  padding: "1px 6px",
                  background: "#e9ecef",
                  borderRadius: "4px",
                  color: "#495057",
                }}
              >
                {getCharCount(form.values.metaKeyword)} ký tự
              </Text>
            </Group>
          </Group>
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label="Mô tả Meta"
            placeholder="Nhập mô tả SEO (khuyến nghị dưới 160 ký tự)..."
            {...form.getInputProps("metaDescription")}
            styles={{
              label: { fontWeight: 600, fontSize: "13px" },
              input: { borderRadius: "6px" },
            }}
          />
          <Group justify="space-between" mt={4} px={2}>
            <Text size="11px" c="dimmed">
              Mô tả ngắn gọn nội dung bài viết hiển thị trên Google
            </Text>
            <Group gap={4}>
              <Text
                size="10px"
                fw={600}
                style={{
                  padding: "1px 6px",
                  background: "#e9ecef",
                  borderRadius: "4px",
                  color: "#495057",
                }}
              >
                {getWordCount(form.values.metaDescription)} từ
              </Text>
              <Text
                size="10px"
                fw={600}
                style={{
                  padding: "1px 6px",
                  background:
                    getCharCount(form.values.metaDescription) > 160
                      ? "#ffe3e3"
                      : "#e9ecef",
                  borderRadius: "4px",
                  color:
                    getCharCount(form.values.metaDescription) > 160
                      ? "#e03131"
                      : "#495057",
                }}
              >
                {getCharCount(form.values.metaDescription)}/160 ký tự
              </Text>
            </Group>
          </Group>
        </Grid.Col>
      </Grid>

      <Group
        justify="center"
        mt="xl"
        style={{ 
          borderTop: "1px solid #dee2e6", 
          padding: "16px 0",
          position: "sticky",
          bottom: -16, // to compensate for modal padding if any, or just 0
          background: "white",
          zIndex: 100,
          marginTop: "16px",
          width: "100%",
        }}
      >
        <Button
          type="submit"
          color="blue"
          leftSection={<IconCheck size={18} />}
          style={{ borderRadius: "6px", height: "36px", padding: "0 24px" }}
        >
          Cập nhật hãng
        </Button>
        <ButtonClose />
      </Group>
    </Box>
  );
};

const getWordCount = (str: string | null | undefined) =>
  str ? str.trim().split(/\s+/).filter(Boolean).length : 0;
const getCharCount = (str: string | null | undefined) => (str ? str.length : 0);

const convertToSlug = (str: string) => {
  str = str.toLowerCase();
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
  str = str.replace(/đ/g, "d");
  str = str.replace(/[^a-z0-9 -]/g, ""); // Remove invalid chars
  str = str.replace(/\s+/g, "-"); // Collapse whitespace and replace by -
  str = str.replace(/-+/g, "-"); // Collapse dashes
  str = str.replace(/^-+/, ""); // Trim - from start of text
  str = str.replace(/-+$/, ""); // Trim - from end of text
  if (str.length > 0) return str;
  return "";
};

export default EditView;
